/**
 * Regions and distribution operators (ОСР) of the object form — the list of
 * `shared` (`REGION_LIST`, `DSO_OPERATOR_LIST`): all 24 oblasts and the city of
 * Kyiv, the DSO by the region. The stored `region` is the Ukrainian name. An
 * operator without a confirmed rate («немає ставки ОСР») gets no potential;
 * telemetry and internal arbitrage work.
 */
import { DSO_OPERATOR_LIST, REGION_LIST } from 'shared';

import type { Locale } from '../i18n/messages.js';

export interface Region {
  /** Stored value of `sites.region`. */
  value: string;
  label: Record<Locale, string>;
  /** Operators of the region, `sites.osr_code`. */
  osr: readonly string[];
}

export const REGIONS: Region[] = REGION_LIST.map((r) => ({
  value: r.value,
  label: { uk: r.uk, en: r.en },
  osr: r.osr,
}));

/** Region of a new object until the client picks theirs. */
export const DEFAULT_REGION = 'Київська';

export function regionOf(value: string | null | undefined): Region | undefined {
  return REGIONS.find((r) => r.value === value);
}

/** «Київська обл.» / «Kyiv region» for the cards; unknown values as stored. */
export function regionLabel(value: string | null | undefined, locale: Locale): string | null {
  if (!value) return null;
  const r = regionOf(value);
  if (!r) return value;
  if (locale === 'uk' && r.value !== 'Київ') return `${r.label.uk} обл.`;
  return r.label[locale];
}

export function osrName(code: string | null | undefined, locale: Locale): string | null {
  if (!code) return null;
  return DSO_OPERATOR_LIST[code]?.[locale] ?? code;
}

/** The operator has a confirmed network rate (the potential is calculated). */
export function osrHasRate(code: string | null | undefined): boolean {
  return !!code && Object.hasOwn(DSO_OPERATOR_LIST, code) && !!DSO_OPERATOR_LIST[code]?.hasRate;
}
